<script setup lang="ts">
import {computed, ref} from "vue";
import {useEventListener, useNow} from "@vueuse/core";
import {BellRing, Cake} from "@lucide/vue";
import {AgendaAppointment} from "@/core/interfaces/AgendaAppointment";
import {AgendaRenewalReminder} from "@/core/interfaces/AgendaRenewalReminder";
import {AgendaBirthday} from "@/core/interfaces/AgendaBirthday";
import {birthdayChipLabel, birthdayDateKey, birthdayNames} from "@/agenda/lib/birthdays";
import {FrequentationWeek} from "@/core/interfaces/Frequentation";
import {AgendaLane, PositionedLane, positionLanes} from "@/agenda/lib/lanes";
import {AgendaPresenceLane} from "@/agenda/lib/presence";
import {parseAgendaDate} from "@/agenda/lib/agendaTime";
import {fallsOnDay} from "@/agenda/lib/agendaViews";
import {colorForLevel, describeDay, describeSlot} from "@/agenda/lib/frequentation";
import {closedRangesOn} from "@/agenda/lib/openingHours";
import {HealthcenterOpening} from "@/core/interfaces/healthcenter/Healthcenter";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import AgendaAppointmentCard from "@/agenda/components/AgendaAppointmentCard.vue";
import AgendaReminderChip from "@/agenda/components/AgendaReminderChip.vue";
import {
    DAY_END_HOUR,
    DAY_START_HOUR,
    FREQUENTATION_BACKGROUND_OPACITY,
    FREQUENTATION_COLUMN_WIDTH_PX,
    HOUR_AXIS_WIDTH_PX,
    HOUR_HEIGHT_PX,
    LANE_MIN_WIDTH_PX,
    SLOT_MINUTES
} from "@/agenda/constants";

/**
 * Hour grid shared by the day, week and team views: an hour axis plus one
 * column per lane. Only the lane composition differs between the three.
 */
const props = defineProps<{
    lanes: AgendaLane[];
    /**
     * Renewal reminders of the displayed window. They have no hour slot to
     * occupy — they are sent once, at the same time for everyone — so they live
     * in a band above the grid rather than in it.
     */
    reminders?: AgendaRenewalReminder[];
    /**
     * Targeted patient birthdays of the window, shown as one aggregate chip
     * per day rather than one per patient: a pharmacy celebrates a dozen a
     * day, and the band would swallow the hour grid.
     */
    birthdays?: AgendaBirthday[];
    /** Narrow columns: cards drop to their one-line layout. */
    dense?: boolean;
    /** Allow vertical dragging of the cards to reschedule them. */
    draggable?: boolean;
    /** Team presence rail of the day view, left of the hour axis. */
    presence?: AgendaPresenceLane[];
    /** The rail is being refreshed: shown, but not asserted as current. */
    presencePending?: boolean;
    /** Expected load of the displayed week, to overlay. */
    frequentation?: FrequentationWeek | null;
    /**
     * How the load shows: a colored strip beside the hour axis (day and team
     * views, whose columns share one day), or a tinted background per column
     * (week view). Absent, no overlay at all.
     */
    frequentationDisplay?: 'column' | 'background';
    /**
     * Opening sheet of the pharmacy: its closing hours are greyed in every
     * column, as information only. A slot stays clickable while the pharmacy
     * is closed — the counter decides, not the sheet. Absent, nothing is greyed.
     */
    opening?: HealthcenterOpening | null;
}>();

const emits = defineEmits<{
    (e: 'slot-click', payload: {start: Date; operatorAccessId?: number | null}): void;
    (e: 'appointment-click', appointment: AgendaAppointment): void;
    (e: 'reschedule-request', payload: {appointment: AgendaAppointment; start: Date}): void;
    (e: 'reminder-click', reminder: AgendaRenewalReminder): void;
    (e: 'birthdays-click', day: Date): void;
    (e: 'presence-click', memberId: number): void;
}>();

/** Width of one member column of the presence rail. */
const PRESENCE_MEMBER_WIDTH_PX = 34;

const presenceLanes = computed(() => props.presence ?? []);

/**
 * Ranges only count once the data is confirmed. While refreshing they are
 * resolved against the previous period's absences, so letting them stretch
 * the hour bounds would rescale the whole grid twice per navigation.
 */
const trustedPresenceRanges = computed(() =>
    props.presencePending ? [] : presenceLanes.value.flatMap((lane) => lane.ranges)
);

const presenceWidth = computed(() =>
    presenceLanes.value.length ? presenceLanes.value.length * PRESENCE_MEMBER_WIDTH_PX + 6 : 0
);

/** Left offset of the columns: the presence rail sits before the hour axis. */
const axisOffset = computed(() => presenceWidth.value + HOUR_AXIS_WIDTH_PX);

const positioned = computed(() => positionLanes(props.lanes));

const hasHeaders = computed(() => props.lanes.some((lane) => !!lane.label));

const hasReminders = computed(() => !!props.reminders?.length);

const hasBirthdays = computed(() => !!props.birthdays?.length);

/**
 * Birthdays celebrated on a lane's day. Compared on the date key, not on the
 * instant: a celebration is a day.
 */
const birthdaysOf = (lane: AgendaLane) => {
    const key = birthdayDateKey(lane.day);

    return (props.birthdays ?? []).filter((birthday) => birthday.celebrated_on === key);
};

/**
 * Whether every column shows the same day — the day view with its single
 * column, and the team view where a column is a collaborator. The reminders
 * then belong to the grid as a whole, and repeating them per column would
 * just say the same thing N times.
 */
const sharedDay = computed(() => {
    const [first] = props.lanes;

    return !!first && props.lanes.every((lane) => parseAgendaDate(lane.day).isSame(parseAgendaDate(first.day), 'day'));
});

/**
 * Reminders sent on a lane's day, for the per-column band of the week view.
 */
const remindersOf = (lane: AgendaLane) => {
    return (props.reminders ?? []).filter((reminder) => fallsOnDay(reminder, parseAgendaDate(lane.day)));
};

/**
 * Hour bounds shared by every column, so the rows stay aligned across lanes:
 * the defaults, extended to cover any appointment falling outside.
 */
const startHour = computed(() => Math.min(
    DAY_START_HOUR,
    ...positioned.value.flatMap((lane) => lane.entries.map((entry) => Math.floor(entry.start / 60))),
    ...trustedPresenceRanges.value.map((range) => Math.floor(range.start / 60))
));

const endHour = computed(() => Math.max(
    DAY_END_HOUR,
    ...positioned.value.flatMap((lane) => lane.entries.map((entry) => Math.ceil(entry.end / 60))),
    ...trustedPresenceRanges.value.map((range) => Math.ceil(range.end / 60))
));

const hours = computed(() => Array.from(
    {length: endHour.value - startHour.value},
    (_, index) => startHour.value + index
));

const totalMinutes = computed(() => (endHour.value - startHour.value) * 60);
const gridHeight = computed(() => hours.value.length * HOUR_HEIGHT_PX);

const showFrequentationColumn = computed(() => {
    return props.frequentationDisplay === 'column' && !!props.frequentation && !!props.lanes.length;
});

const showFrequentationBackground = computed(() => {
    return props.frequentationDisplay === 'background' && !!props.frequentation;
});

/**
 * Colored hour segments of the load overlay, per day (Y-m-d): position
 * shared with the cards, color from the slot's level, and the slot's
 * description for the tooltip. Hours the history says nothing about stay
 * blank. Computed once per data change, not per render: the grid re-renders
 * on every pointer move of a drag, and this is dayjs parsing plus number
 * formatting for every hour of every column.
 */
const frequentationSegmentsByDate = computed(() => {
    const segments = new Map<string, {hour: number; description: {title: string; lines: string[]}; style: Record<string, string>}[]>();
    const frequentation = props.frequentation;

    if (!frequentation) {
        return segments;
    }

    const shown = new Set(hours.value);

    frequentation.days.forEach((day) => {
        segments.set(day.date, day.hours
            .filter((slot) => shown.has(slot.hour))
            .map((slot) => ({
                hour: slot.hour,
                description: describeSlot(slot, frequentation.average_minutes),
                style: {
                    top: `${percentOfMinute(slot.hour * 60)}%`,
                    height: `${(60 / totalMinutes.value) * 100}%`,
                    backgroundColor: colorForLevel(slot.level)
                }
            })));
    });

    return segments;
});

/**
 * What a week column's header says about its day's load, per day (Y-m-d):
 * the background segments are inert so slot clicks and drags pass through,
 * so the header carries the tooltip instead.
 */
const frequentationDayDescriptionByDate = computed(() => {
    const descriptions = new Map<string, {title: string; lines: string[]}>();

    if (showFrequentationBackground.value) {
        props.frequentation?.days.forEach((day) => descriptions.set(day.date, describeDay(day)));
    }

    return descriptions;
});

/** The Y-m-d key of each lane's day, the join with the two maps above. */
const laneDateKeys = computed(() => new Map(positioned.value.map((lane) => [lane.key, parseAgendaDate(lane.day).format('YYYY-MM-DD')])));

const frequentationSegmentsOf = (lane: PositionedLane) => frequentationSegmentsByDate.value.get(laneDateKeys.value.get(lane.key) ?? '') ?? [];

const frequentationDayDescription = (lane: PositionedLane) => frequentationDayDescriptionByDate.value.get(laneDateKeys.value.get(lane.key) ?? '') ?? null;

/**
 * Grey segments of the closing hours, per day (Y-m-d): the sheet's closed
 * ranges clipped to the hours on screen, positioned like the cards. Computed
 * per displayed day rather than per column, as the team view repeats one day
 * over every column.
 */
const closedSegmentsByDate = computed(() => {
    const segments = new Map<string, {start: number; style: Record<string, string>}[]>();
    const first = startHour.value * 60;
    const last = endHour.value * 60;

    positioned.value.forEach((lane) => {
        const key = laneDateKeys.value.get(lane.key) ?? '';

        if (segments.has(key)) {
            return;
        }

        segments.set(key, closedRangesOn(props.opening, parseAgendaDate(lane.day))
            .map((range) => ({start: Math.max(range.start, first), end: Math.min(range.end, last)}))
            .filter((range) => range.start < range.end)
            .map((range) => ({
                start: range.start,
                style: {
                    top: `${percentOfMinute(range.start)}%`,
                    height: `${((range.end - range.start) / totalMinutes.value) * 100}%`
                }
            })));
    });

    return segments;
});

const closedSegmentsOf = (lane: PositionedLane) => closedSegmentsByDate.value.get(laneDateKeys.value.get(lane.key) ?? '') ?? [];

/**
 * Width both rows are laid out against. Below it the grid scrolls horizontally
 * rather than squeezing the columns — which matters in the team view, where a
 * pharmacy can field a dozen of them.
 */
/**
 * Width of the frequentation strip, when drawn: it sits between the hour
 * axis and the columns, after the presence rail.
 */
const frequentationWidth = computed(() => showFrequentationColumn.value ? FREQUENTATION_COLUMN_WIDTH_PX : 0);

const gridMinWidth = computed(() => axisOffset.value + frequentationWidth.value + props.lanes.length * LANE_MIN_WIDTH_PX);

const percentOfMinute = (minute: number) => {
    return ((minute - startHour.value * 60) / totalMinutes.value) * 100;
};

const cardsOf = (lane: typeof positioned.value[number]) => {
    return lane.entries.map((entry) => {
        const width = 100 / entry.columns;

        return {
            appointment: entry.item,
            style: {
                top: `${percentOfMinute(entry.start)}%`,
                height: `${(Math.max(entry.end - entry.start, SLOT_MINUTES) / totalMinutes.value) * 100}%`,
                left: `${entry.column * width}%`,
                width: `calc(${width}% - 4px)`
            }
        };
    });
};

const now = useNow({interval: 60 * 1000});

/**
 * Now line, rendered only in the columns actually showing today.
 */
const nowPercentFor = (day: Date) => {
    const current = parseAgendaDate(now.value);

    if (!current.isSame(parseAgendaDate(day), 'day')) {
        return null;
    }

    const percent = percentOfMinute(current.hour() * 60 + current.minute());

    return percent >= 0 && percent <= 100 ? percent : null;
};

/** Pixels a pointer must travel before the gesture counts as a drag. */
const DRAG_THRESHOLD_PX = 4;

const MINUTES_PER_DAY = 24 * 60;

/**
 * In-flight drag. The offsets are a preview only: nothing is persisted until
 * the parent's confirmation, so releasing or cancelling leaves the card
 * exactly where it was.
 */
const drag = ref<{
    appointment: AgendaAppointment;
    startY: number;
    offsetMinutes: number;
    originLane: number;
    targetLane: number;
    moved: boolean;
} | null>(null);

/** Lane columns, hit-tested to find which one a drag is released over. */
const laneElements = ref<(HTMLElement | null)[]>([]);

const setLaneElement = (element: unknown, index: number) => {
    laneElements.value[index] = (element as HTMLElement | null) ?? null;
};

const draggedIs = (appointment: AgendaAppointment) => drag.value?.appointment.id === appointment.id;

/**
 * Preview translation of the dragged card: vertical for the new time,
 * horizontal to sit over the column the pointer is on, so the target day is
 * unambiguous while dragging in the week view.
 */
const previewTransformOf = (appointment: AgendaAppointment) => {
    if (!drag.value || !draggedIs(appointment)) {
        return undefined;
    }

    const y = (drag.value.offsetMinutes / totalMinutes.value) * gridHeight.value;
    const origin = laneElements.value[drag.value.originLane]?.getBoundingClientRect();
    const target = laneElements.value[drag.value.targetLane]?.getBoundingClientRect();
    const x = origin && target ? target.left - origin.left : 0;

    return `translate(${x}px, ${y}px)`;
};

/**
 * Column under the pointer, falling back to the column the drag started from
 * when the pointer wanders outside the grid.
 */
const laneIndexAt = (clientX: number, fallback: number) => {
    const index = laneElements.value.findIndex((element) => {
        if (!element) {
            return false;
        }

        const rect = element.getBoundingClientRect();

        return clientX >= rect.left && clientX <= rect.right;
    });

    return index === -1 ? fallback : index;
};

/**
 * `pointerup` fires before `click`, so the drag state is already cleared when
 * the click handler runs: a dedicated flag is what keeps a drag from also
 * opening the detail modal.
 */
let suppressNextClick = false;

const startDrag = (appointment: AgendaAppointment, laneIndex: number, event: PointerEvent) => {
    // Cleared before the guard: a click always follows a pointerdown, so this
    // is what keeps a suppression left over by a drag released outside its
    // card from swallowing an unrelated click later on.
    suppressNextClick = false;

    if (!props.draggable) {
        return;
    }

    // Capture keeps the moves coming when the pointer leaves the card. It
    // throws on an inactive pointer id, which must not abort the drag itself.
    try {
        (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    } catch {
        // Without capture the drag still works while the pointer stays inside.
    }

    drag.value = {
        appointment,
        startY: event.clientY,
        offsetMinutes: 0,
        originLane: laneIndex,
        targetLane: laneIndex,
        moved: false
    };
};

const moveDrag = (event: PointerEvent) => {
    if (!drag.value) {
        return;
    }

    const deltaY = event.clientY - drag.value.startY;
    const previousTarget = drag.value.targetLane;

    drag.value.targetLane = laneIndexAt(event.clientX, drag.value.originLane);

    if (Math.abs(deltaY) >= DRAG_THRESHOLD_PX || drag.value.targetLane !== previousTarget) {
        drag.value.moved = true;
    }

    const minutes = (deltaY / HOUR_HEIGHT_PX) * 60;

    drag.value.offsetMinutes = Math.round(minutes / SLOT_MINUTES) * SLOT_MINUTES;
};

const endDrag = () => {
    const current = drag.value;

    drag.value = null;

    if (!current?.moved) {
        // A tap: let the click handler open the detail modal.
        return;
    }

    const appointment = current.appointment;

    suppressNextClick = true;

    const laneChanged = current.targetLane !== current.originLane;

    // Dragged back onto its own slot: nothing to reschedule.
    if (!current.offsetMinutes && !laneChanged) {
        return;
    }

    const targetLane = positioned.value[current.targetLane];

    if (!targetLane) {
        return;
    }

    const origin = parseAgendaDate(appointment.start_at);

    // Wall-clock composition on the target day: the hour/minute setters stay
    // correct on DST-change days, and clamping keeps the drop inside that day
    // rather than silently spilling into the next one.
    const minutesOfDay = Math.min(
        MINUTES_PER_DAY - SLOT_MINUTES,
        Math.max(0, origin.hour() * 60 + origin.minute() + current.offsetMinutes)
    );

    const start = parseAgendaDate(targetLane.day)
        .startOf('day')
        .hour(Math.floor(minutesOfDay / 60))
        .minute(minutesOfDay % 60);

    emits('reschedule-request', {appointment, start: start.toDate()});
};

/**
 * Move, release and cancel are watched on the window, not on the card: pointer
 * capture can be refused, and a release landing anywhere but the card would
 * then leave the drag state set — the card frozen in its preview, and every
 * later hover still moving that same appointment.
 */
useEventListener(window, 'pointermove', moveDrag);
useEventListener(window, 'pointerup', endDrag);
useEventListener(window, 'pointercancel', () => {
    drag.value = null;
});

const handleCardClick = (appointment: AgendaAppointment) => {
    if (suppressNextClick) {
        suppressNextClick = false;

        return;
    }

    emits('appointment-click', appointment);
};

/**
 * Turn a click on an hour cell into a slot start on that lane's day, snapped
 * to the grid step. offsetY is relative to the clicked cell, and the
 * hour()/minute() setters stay correct on DST-change days.
 */
const handleSlotClick = (lane: PositionedLane, hour: number, event: MouseEvent) => {
    if (lane.readonly) {
        return;
    }

    const minutesInHour = (event.offsetY / HOUR_HEIGHT_PX) * 60;
    const snapped = Math.floor(minutesInHour / SLOT_MINUTES) * SLOT_MINUTES;

    emits('slot-click', {
        start: parseAgendaDate(lane.day).startOf('day').hour(hour).minute(snapped).toDate(),
        operatorAccessId: lane.operatorAccessId
    });
};
</script>

<template>
  <!--
    The headers live inside the scroll container, sticky: outside of it they
    would keep the scrollbar's width while the columns lose it, and the two rows
    would drift apart on any platform whose scrollbars take layout space.
  -->
  <TooltipProvider>
    <div class="h-full overflow-auto">
      <!--
        Both rows are sized against this wrapper, so a header always sits above
        its own column. Sizing them independently is what desynchronised them:
        the header labels are nowrap text, whose intrinsic width pushed that row
        wider than the columns row, whose cells have no intrinsic content at all.
        Hence min-w-0 on every cell — the explicit widths here are the only ones.
      -->
      <div class="flex flex-col" :style="{minWidth: `${gridMinWidth}px`}">
        <div v-if="hasHeaders || hasReminders || hasBirthdays || presenceLanes.length" class="sticky top-0 z-20 bg-white">
          <div v-if="hasHeaders" class="flex border-b border-gray-200">
            <div class="shrink-0" :style="{width: `${axisOffset}px`}" />
            <!-- Mirrors the frequentation strip so headers stay over their column. -->
            <div v-if="showFrequentationColumn" class="shrink-0" :style="{width: `${FREQUENTATION_COLUMN_WIDTH_PX}px`}" />
            <Tooltip v-for="lane in positioned" :key="lane.key" :delay-duration="200">
              <TooltipTrigger as-child>
                <div class="min-w-0 flex-1 border-l border-gray-100 px-2 py-2 text-center">
                  <!-- first-letter, not capitalize: the latter would also recase the
                       words of a person's name in the team view. -->
                  <p class="truncate text-sm font-medium first-letter:uppercase">
                    {{ lane.label }}
                  </p>
                  <p class="truncate text-xs text-gray-500">
                    {{ lane.sublabel }}
                  </p>
                </div>
              </TooltipTrigger>
              <TooltipContent v-if="frequentationDayDescription(lane)" side="bottom" class="max-w-xs text-xs">
                <p class="font-semibold">
                  {{ frequentationDayDescription(lane)?.title }}
                </p>
                <p v-for="line in frequentationDayDescription(lane)?.lines" :key="line">
                  {{ line }}
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <!--
            Reminder band. One cell across the grid when every column shows the
            same day, one cell per column otherwise — same min-w-0 flex-1 cells
            as the columns below, so a chip stays over its own day.
          -->
          <div v-if="hasReminders" class="flex border-b border-gray-200">
            <div
              class="sticky left-0 z-10 flex shrink-0 items-center justify-center bg-white"
              :style="{width: `${axisOffset}px`}"
              title="Rappels de renouvellement d'ordonnance"
            >
              <BellRing class="size-4 text-gray-400" />
            </div>
            <div v-if="showFrequentationColumn" class="shrink-0" :style="{width: `${FREQUENTATION_COLUMN_WIDTH_PX}px`}" />
            <div v-if="sharedDay" class="flex min-w-0 flex-1 flex-col gap-1 border-l border-gray-100 p-1.5">
              <AgendaReminderChip
                v-for="reminder in reminders"
                :key="reminder.id"
                :reminder="reminder"
                @click="emits('reminder-click', reminder)"
              />
            </div>
            <div
              v-for="lane in positioned"
              v-else
              :key="lane.key"
              class="flex min-w-0 flex-1 flex-col gap-1 border-l border-gray-100 p-1.5"
            >
              <AgendaReminderChip
                v-for="reminder in remindersOf(lane)"
                :key="reminder.id"
                :reminder="reminder"
                compact
                @click="emits('reminder-click', reminder)"
              />
            </div>
          </div>
          <!--
            Birthday band. One aggregate chip per day, the count on the grid and
            the people in the tooltip: a counter screen must not display patient
            names in passing.
          -->
          <div v-if="hasBirthdays" class="flex border-b border-gray-200">
            <div
              class="sticky left-0 z-10 flex shrink-0 items-center justify-center bg-white"
              :style="{width: `${axisOffset}px`}"
              title="Anniversaires des patients"
            >
              <Cake class="size-4 text-pink" />
            </div>
            <div v-if="showFrequentationColumn" class="shrink-0" :style="{width: `${FREQUENTATION_COLUMN_WIDTH_PX}px`}" />
            <div
              v-for="lane in positioned"
              :key="`birthdays-${lane.key}`"
              class="flex min-w-0 flex-1 flex-col justify-center border-l border-gray-100 p-1.5"
            >
              <button
                v-if="birthdaysOf(lane).length"
                type="button"
                class="flex w-full items-center gap-1.5 rounded-md bg-pink/20 px-2 py-1 text-left text-xs font-medium text-gray-dark transition-colors hover:bg-pink/30"
                :title="birthdayNames(birthdaysOf(lane))"
                @click="emits('birthdays-click', lane.day)"
              >
                <Cake class="size-3 shrink-0" />
                <span class="truncate">{{ birthdayChipLabel(birthdaysOf(lane).length) }}</span>
              </button>
            </div>
          </div>
          <!-- Presence rail avatars, each above its member's track below. -->
          <div v-if="presenceLanes.length" class="flex border-b border-gray-200">
            <div
              class="sticky left-0 z-10 flex shrink-0 bg-white pb-1 pl-1 pt-1.5 transition-opacity"
              :class="presencePending ? 'opacity-40' : ''"
              :style="{width: `${presenceWidth}px`}"
            >
              <div
                v-for="lane in presenceLanes"
                :key="lane.key"
                class="flex justify-center"
                :style="{width: `${PRESENCE_MEMBER_WIDTH_PX}px`}"
              >
                <button
                  class="relative flex size-7 items-center justify-center rounded-full text-[9px] font-semibold text-white"
                  :style="{backgroundColor: lane.color}"
                  :title="`${lane.name} — cliquer pour ouvrir le planning`"
                  @click="emits('presence-click', lane.key)"
                >
                  {{ lane.initials }}
                  <span
                    v-if="!presencePending"
                    class="absolute -bottom-px -right-px size-2 rounded-full border-[1.5px] border-white"
                    :class="lane.presentNow ? 'bg-green-500' : 'bg-gray-300'"
                  />
                </button>
              </div>
            </div>
            <div class="min-w-0 flex-1" />
          </div>
        </div>
        <!-- pt-2 clears the first hour label, which straddles its line and would
             otherwise sit under the sticky header. -->
        <div class="flex pt-2">
          <!-- Presence tracks, sharing the grid's vertical scale row for row. -->
          <div
            v-if="presenceLanes.length"
            class="sticky left-0 z-[15] flex shrink-0 bg-white pl-1 transition-opacity"
            :class="presencePending ? 'opacity-40' : ''"
            :style="{height: `${gridHeight}px`, width: `${presenceWidth}px`}"
          >
            <div
              v-for="lane in presenceLanes"
              :key="lane.key"
              class="flex justify-center"
              :style="{width: `${PRESENCE_MEMBER_WIDTH_PX}px`}"
            >
              <div class="relative h-full w-3 overflow-hidden rounded bg-gray-100">
                <button
                  v-for="(range, index) in (presencePending ? [] : lane.ranges)"
                  :key="index"
                  class="absolute inset-x-0 transition-[filter] hover:brightness-110"
                  :style="{
                    top: `${percentOfMinute(range.start)}%`,
                    height: `${percentOfMinute(range.end) - percentOfMinute(range.start)}%`,
                    backgroundColor: lane.color
                  }"
                  :title="`${lane.name} · ${range.label} — cliquer pour ajuster dans le planning`"
                  @click="emits('presence-click', lane.key)"
                />
              </div>
            </div>
          </div>
          <!-- Sticky so the hours stay readable once the columns scroll sideways.
               Below the header's z-20, above the now line drawn in the lanes. -->
          <div
            class="sticky z-[15] shrink-0 select-none bg-white"
            :style="{height: `${gridHeight}px`, width: `${HOUR_AXIS_WIDTH_PX}px`, left: `${presenceWidth}px`}"
          >
            <div
              v-for="hour in hours"
              :key="hour"
              class="relative text-right text-xs text-gray-400"
              :style="{height: `${HOUR_HEIGHT_PX}px`}"
            >
              <span class="absolute -top-2 right-2 tabular-nums">{{ `${hour}:00` }}</span>
            </div>
          </div>
          <!--
            Day view frequentation strip (freqCol): one colored segment per hour
            the pharmacy's history grades. Sticky with the hour axis, so the
            expected attendance stays readable while the columns scroll.
          -->
          <div
            v-if="showFrequentationColumn"
            class="sticky z-[15] shrink-0 select-none bg-white"
            :style="{left: `${axisOffset}px`, width: `${FREQUENTATION_COLUMN_WIDTH_PX}px`, height: `${gridHeight}px`}"
          >
            <div class="relative h-full">
              <Tooltip v-for="segment in frequentationSegmentsOf(positioned[0])" :key="segment.hour" :delay-duration="200">
                <TooltipTrigger as-child>
                  <div class="absolute inset-x-px rounded-sm" :style="segment.style" />
                </TooltipTrigger>
                <TooltipContent side="right" class="max-w-xs text-xs">
                  <p class="font-semibold">
                    {{ segment.description.title }}
                  </p>
                  <p v-for="line in segment.description.lines" :key="line">
                    {{ line }}
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
          <div
            v-for="(lane, laneIndex) in positioned"
            :ref="(element) => setLaneElement(element, laneIndex)"
            :key="lane.key"
            class="relative min-w-0 flex-1 border-l border-gray-100 transition-colors"
            :class="[
              drag && drag.targetLane === laneIndex && drag.targetLane !== drag.originLane ? 'bg-primary-lightest/40' : '',
              lane.readonly ? 'bg-gray-50/60' : ''
            ]"
            :style="{height: `${gridHeight}px`}"
          >
            <!-- No slot affordance on a column no appointment can belong to. -->
            <div
              v-for="hour in hours"
              :key="hour"
              class="border-t border-gray-100 transition-colors"
              :class="lane.readonly ? 'cursor-default' : 'cursor-pointer hover:bg-gray-50'"
              :style="{height: `${HOUR_HEIGHT_PX}px`}"
              @click="handleSlotClick(lane, hour, $event)"
            />
            <!--
              Week view frequentation: the column background carries the level
              color. Painted over the hour cells but inert, so slot clicks and
              drags go through; the cards and now line stack above.
            -->
            <template v-if="showFrequentationBackground">
              <div
                v-for="segment in frequentationSegmentsOf(lane)"
                :key="`frequentation-${segment.hour}`"
                class="pointer-events-none absolute inset-x-0"
                :style="{...segment.style, opacity: FREQUENTATION_BACKGROUND_OPACITY}"
              />
            </template>
            <!--
              Closing hours of the pharmacy, greyed over the column. Above the
              load tint, which only means something while the counter is open,
              and inert like it: the slot underneath stays clickable. Blended by
              multiplication so the hour lines show through the grey.
            -->
            <div
              v-for="segment in closedSegmentsOf(lane)"
              :key="`closed-${segment.start}`"
              class="pointer-events-none absolute inset-x-0 bg-gray-200/70 mix-blend-multiply"
              :style="segment.style"
            />
            <div
              v-if="nowPercentFor(lane.day) !== null"
              class="pointer-events-none absolute inset-x-0 z-10 border-t-2 border-red-500"
              :style="{top: `${nowPercentFor(lane.day)}%`}"
            >
              <span class="absolute -left-1 -top-1.5 size-2.5 rounded-full bg-red-500" />
            </div>
            <AgendaAppointmentCard
              v-for="entry in cardsOf(lane)"
              :key="entry.appointment.id"
              :appointment="entry.appointment"
              :compact="dense"
              class="absolute transition-shadow hover:shadow-md"
              :class="[
                draggable ? 'cursor-grab touch-none active:cursor-grabbing' : 'cursor-pointer',
                draggedIs(entry.appointment) ? 'z-30 opacity-80 shadow-lg' : ''
              ]"
              :style="{...entry.style, transform: previewTransformOf(entry.appointment)}"
              @pointerdown="startDrag(entry.appointment, laneIndex, $event)"
              @click="handleCardClick(entry.appointment)"
            />
          </div>
        </div>
      </div>
    </div>
  </TooltipProvider>
</template>
